import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {FaArrowLeft} from 'react-icons/fa';
import {collection, doc, getDoc, getDocs} from 'firebase/firestore';
import {db} from '../utils/firebase';
import {useAuth} from '../context/AuthContext';
import {useLang} from '../context/LanguageContext';
import {DEFAULT_CODE_GEN_ENABLED} from '../utils/codeGen';
import Footer from '../components/Footer';
import Loading from '../components/Loading';
import Lottie from 'lottie-react';
import loadingAnimation from '../lotties/loading_ball.json';

// durata animatiei de "generare" (ms)
const GENERATION_ANIMATION_MS = 7000;

// intervale de cota totala, calculate din datele reale ale biletelor (nu din oddCategory)
const ODDS_INTERVALS = [
    {key: '0-10', min: 0, max: 10, label: '< 10'},
    {key: '10-50', min: 10, max: 50, label: '10 - 50'},
    {key: '50-100', min: 50, max: 100, label: '50 - 100'},
    {key: '100-500', min: 100, max: 500, label: '100 - 500'},
    {key: '500-1000', min: 500, max: 1000, label: '500 - 1000'},
    {key: '1000+', min: 1000, max: Infinity, label: '1000+'},
];

const oddInRange = (totalOdd, range) => {
    const odd = Number(totalOdd);
    return !isNaN(odd) && odd >= range.min && odd < range.max;
};

const formatMatchDay = (dayStr) => {
    if (!dayStr) return '';
    const date = new Date(dayStr);
    if (isNaN(date.getTime())) return dayStr;
    return date.toLocaleDateString('ro-RO', {day: '2-digit', month: 'short'});
};

// pentru userii fara abonament nu trimitem in browser datele reale ale biletului
// (codul/echipele/piata sunt continutul platit) - doar un preview mascat, cu acelasi
// numar de meciuri, ca blur-ul CSS sa nu poata fi ocolit din inspect element
const buildLockedPreview = (realTicket) => ({
    code: '••••-••••••',
    totalOdd: '••.••',
    matches: (realTicket.matches || []).map(() => ({
        team1: '••••••••',
        team2: '••••••••',
        day: '',
        hour: '••:••',
        marketName: '••••••',
        market: '••••',
        odd: '•.••',
    })),
});

const CodeTicketPage = () => {
    const navigate = useNavigate();
    const {t, getBetTranslation} = useLang();
    const {currentUser, hasCodeTicketAccess} = useAuth();
    const isUnlocked = !!currentUser && hasCodeTicketAccess;

    const [enabled, setEnabled] = useState(DEFAULT_CODE_GEN_ENABLED);
    const [settingsLoaded, setSettingsLoaded] = useState(false);

    const [allTickets, setAllTickets] = useState(null);
    const [ticketsError, setTicketsError] = useState('');
    const [selectedInterval, setSelectedInterval] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);

    const [ticket, setTicket] = useState(null);
    const [hasGenerated, setHasGenerated] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);

    // starea sectiunii (blocata/deblocata din admin)
    useEffect(() => {
        const loadSettings = async () => {
            try {
                const snap = await getDoc(doc(db, 'appSettings', 'codeGeneration'));
                if (snap.exists() && typeof snap.data().enabled === 'boolean') {
                    setEnabled(snap.data().enabled);
                }
            } catch (err) {
                // pastram valoarea default daca nu putem citi
            }
            setSettingsLoaded(true);
        };
        loadSettings();
    }, []);

    // biletele disponibile, incarcate o singura data, ca sa putem construi categoriile de cota
    // (doar pentru useri logati - altfel Firestore respinge citirea si nu are rost sa incercam)
    useEffect(() => {
        if (!settingsLoaded || !enabled || !currentUser) return;

        const loadTickets = async () => {
            try {
                const snap = await getDocs(collection(db, 'tickets'));
                const list = [];
                snap.forEach(d => list.push(d.data()));
                setAllTickets(list);
            } catch (err) {
                console.error(err);
                setTicketsError(t('txt.codeGenError'));
                setAllTickets([]);
            }
        };
        loadTickets();
    }, [settingsLoaded, enabled, currentUser, t]);

    const intervals = useMemo(() => {
        if (!allTickets) return [];
        return ODDS_INTERVALS
            .map(range => ({
                ...range,
                count: allTickets.filter(tk => oddInRange(tk.totalOdd, range)).length,
            }))
            .filter(range => range.count > 0);
    }, [allTickets]);

    const periods = useMemo(() => {
        if (!allTickets) return [];
        return Array.from(new Set(allTickets.map(tk => tk.period).filter(Boolean)));
    }, [allTickets]);

    const handleGenerate = async () => {
        if (!enabled || !selectedInterval || !selectedPeriod) return;
        setGenerating(true);
        setHasGenerated(true);
        setError('');
        setCopied(false);
        setTicket(null);

        // animatia ruleaza minim GENERATION_ANIMATION_MS, in paralel cu selectia
        const animation = new Promise(res => setTimeout(res, GENERATION_ANIMATION_MS));

        const range = ODDS_INTERVALS.find(r => r.key === selectedInterval);
        const matching = (allTickets || []).filter(tk =>
            range && oddInRange(tk.totalOdd, range) && tk.period === selectedPeriod
        );
        let result = null;
        let errMsg = '';
        if (matching.length === 0) {
            errMsg = t('txt.codeGenEmpty');
        } else {
            result = matching[Math.floor(Math.random() * matching.length)];
        }

        await animation;
        if (errMsg) {
            setError(errMsg);
        } else {
            setTicket(isUnlocked ? result : buildLockedPreview(result));
        }
        setGenerating(false);
    };

    const handleCopy = async () => {
        if (!ticket?.code) return;
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(ticket.code);
            } else {
                // fallback pentru browsere mobile vechi / context nesecurizat
                const textarea = document.createElement('textarea');
                textarea.value = ticket.code;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
        } catch (err) {
            // daca ambele metode esueaza, afisam oricum feedback (userul poate copia manual)
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (!settingsLoaded) {
        return (
            <div className="relative min-h-screen bg-[#34495e] p-5">
                <Loading/>
            </div>
        );
    }

    const noTicketsAtAll = enabled && !ticketsError && allTickets !== null && allTickets.length === 0;

    return (
        <div className="bg-[#34495e] min-h-screen p-5 relative">
            <button
                onClick={() => navigate('/')}
                className="absolute top-5 left-5 px-4 py-2 bg-[#2ecc71] rounded flex items-center space-x-2"
            >
                <FaArrowLeft/>
                <span className="text-white">{t('btn.back')}</span>
            </button>

            <h1 className="text-xl text-white font-bold text-center mt-16 mb-6">
                {t('txt.codeGenSectionTitle')}
            </h1>

            <div className="max-w-[500px] mx-auto">
                {!enabled && (
                    <p className="text-center text-yellow-300 text-sm mb-3">
                        {t('txt.codeGenBlocked')}
                    </p>
                )}

                {enabled && !currentUser && (
                    <div className="text-center space-y-3">
                        <p className="text-yellow-300 text-sm">
                            {t('txt.codeGenLoginRequired')}
                        </p>
                        <button
                            onClick={() => navigate('/login')}
                            className="px-5 py-2.5 bg-[#f39c12] hover:bg-orange-600 text-white font-bold rounded-lg shadow-lg"
                        >
                            {t('btn.login')}
                        </button>
                    </div>
                )}

                {enabled && currentUser && ticketsError && (
                    <p className="text-center text-red-300 text-sm mb-3">{ticketsError}</p>
                )}

                {noTicketsAtAll && (
                    <p className="text-center text-yellow-300 text-lg font-semibold">
                        {t('txt.codeGenInactive')}
                    </p>
                )}

                {enabled && !ticketsError && allTickets !== null && !noTicketsAtAll && (
                    <div className="mb-4">
                        <p className="text-white text-sm font-semibold mb-2">
                            {t('txt.codeGenSelectCategory')}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {intervals.map(range => (
                                <button
                                    key={range.key}
                                    onClick={() => setSelectedInterval(range.key)}
                                    className={
                                        'px-3 py-1.5 rounded-full text-sm font-bold border-2 transition ' +
                                        (selectedInterval === range.key
                                            ? 'bg-[#8e44ad] border-[#8e44ad] text-white'
                                            : 'bg-transparent border-white/40 text-white hover:border-white')
                                    }
                                >
                                    {range.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {enabled && !ticketsError && allTickets !== null && !noTicketsAtAll && (
                    <div className="mb-4">
                        <p className="text-white text-sm font-semibold mb-2">
                            {t('txt.codeGenSelectPeriod')}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {periods.map(period => (
                                <button
                                    key={period}
                                    onClick={() => setSelectedPeriod(period)}
                                    className={
                                        'px-3 py-1.5 rounded-full text-sm font-bold border-2 transition ' +
                                        (selectedPeriod === period
                                            ? 'bg-[#8e44ad] border-[#8e44ad] text-white'
                                            : 'bg-transparent border-white/40 text-white hover:border-white')
                                    }
                                >
                                    {period}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {enabled && !ticketsError && allTickets !== null && !noTicketsAtAll && (
                    <button
                        onClick={handleGenerate}
                        disabled={generating || !selectedInterval || !selectedPeriod}
                        className={
                            'w-full text-white font-bold py-3 rounded shadow transition ' +
                            (selectedInterval && selectedPeriod
                                ? 'bg-[#8e44ad] hover:bg-purple-700 disabled:opacity-70'
                                : 'bg-gray-400 cursor-not-allowed')
                        }
                    >
                        {generating
                            ? t('txt.pleaseWait')
                            : (hasGenerated ? t('txt.codeGenButtonAgain') : t('txt.codeGenButton'))}
                    </button>
                )}

                {error && (
                    <p className="text-center text-red-300 text-sm mt-3">{error}</p>
                )}

                {generating && (
                    <div className="flex flex-col items-center mt-8">
                        <Lottie
                            animationData={loadingAnimation}
                            style={{width: 160, height: 160}}
                        />
                        <p className="text-white font-semibold mt-2">{t('txt.codeGenGenerating')}</p>
                    </div>
                )}

                {!generating && ticket && (
                    <div className="mt-6 relative">
                        <div className={
                            'space-y-4 ' +
                            (isUnlocked ? '' : 'blur-sm select-none pointer-events-none')
                        }>
                            {/* Cod bilet + copiaza */}
                            <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between gap-3">
                                <div>
                                    <p className="text-xs text-gray-500">{t('txt.codeGenCodeLabel')}</p>
                                    <p className="font-mono font-bold text-lg">{ticket.code}</p>
                                </div>
                                {isUnlocked && (
                                    <button
                                        onClick={handleCopy}
                                        className="px-3 py-2 bg-[#2ecc71] hover:bg-green-600 text-white text-sm font-bold rounded whitespace-nowrap"
                                    >
                                        {copied ? t('txt.codeGenCopied') : t('txt.codeGenCopy')}
                                    </button>
                                )}
                            </div>

                            {/* Cota totala */}
                            <div className="text-center">
                                <span className="text-white font-semibold">
                                    {t('txt.totalOdds')}: {ticket.totalOdd}
                                </span>
                            </div>

                            {/* Meciuri */}
                            <div className="space-y-3">
                                {(ticket.matches || []).map((match, idx) => {
                                    const marketName = isUnlocked ? getBetTranslation(match.marketName) : match.marketName;
                                    const market = isUnlocked ? getBetTranslation(match.market) : match.market;
                                    return (
                                        <div key={idx} className="bg-white shadow rounded p-4">
                                            <p className="font-bold text-sm">
                                                {match.team1} vs {match.team2}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {formatMatchDay(match.day)} {match.hour}
                                            </p>
                                            <p className="mt-1 text-sm">
                                                {marketName} ({market}) -{' '}
                                                <span className="font-semibold text-green-600">
                                                    {match.odd}
                                                </span>
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Mesaj avertisment */}
                            <p className="text-center text-gray-300 text-sm italic">
                                {t('txt.codeGenWarning')}
                            </p>
                        </div>

                        {!isUnlocked && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <button
                                    onClick={() => navigate('/subscriptions')}
                                    className="px-5 py-3 bg-[#f39c12] hover:bg-orange-600 text-white font-bold rounded-lg shadow-lg"
                                >
                                    {t('txt.codeGenUnlockButton')}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="mt-8">
                <Footer/>
            </div>
        </div>
    );
};

export default CodeTicketPage;
